import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  TablePagination,
  TextField,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Card,
  CardContent,
  Alert
} from '@mui/material';
import {
  Search as SearchIcon,
  Email as EmailIcon,
  Telegram as TelegramIcon,
  WhatsApp as WhatsAppIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Help as HelpIcon,
  History as HistoryIcon
} from '@mui/icons-material';
import MainLayout from '../../../../components/Layout/MainLayout';
import { useAuth } from '../../../../contexts/AuthContext';
import { getNotifications, NotificationListItem } from './notification-history.api';
import ApiConnectionAlert from '../../../../components/ApiConnectionAlert/ApiConnectionAlert';
import { useTranslation } from 'react-i18next';

// Use API item type directly
type NotificationRecord = NotificationListItem;

const NotificationHistory: React.FC = () => {
  const { isSuperUser } = useAuth();
  const { t } = useTranslation();
  const [notifications, setNotifications] = useState<NotificationRecord[]>([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterFormat, setFilterFormat] = useState<string>('all');
  const [filterSuccess, setFilterSuccess] = useState<string>('all');

  // Load data from API when page or rowsPerPage change
  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const res = await getNotifications(page + 1, rowsPerPage);
        if (!cancelled && res?.success && Array.isArray(res.data)) {
          setNotifications(res.data);
        }
      } catch (err) {
        console.error('Erro ao carregar notificações:', err);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [page, rowsPerPage]);

  const mapTypeDescToKey = (desc?: string): string => {
    const d = (desc || '').toLowerCase();
    if (d.includes('email')) return 'email';
    if (d.includes('telegram')) return 'telegram';
    if (d.includes('whatsapp')) return 'whatsapp';
    return 'other';
  };

  const getNotificationTypeIcon = (typeDesc?: string) => {
    const key = mapTypeDescToKey(typeDesc);
    switch (key) {
      case 'email':
        return <EmailIcon color="primary" />;
      case 'telegram':
        return <TelegramIcon color="info" />;
      case 'whatsapp':
        return <WhatsAppIcon color="success" />;
      default:
        return <HelpIcon />;
    }
  };

  const getNotificationTypeLabel = (typeDesc?: string) => {
    const key = mapTypeDescToKey(typeDesc);
    switch (key) {
      case 'email':
        return t('notification_history_filter_format_email');
      case 'telegram':
        return t('notification_history_filter_format_telegram');
      case 'whatsapp':
        return t('notification_history_filter_format_whatsapp');
      default:
        return typeDesc || '-';
    }
  };

  const getSuccessIcon = (success: boolean) => {
    return success ? (
      <CheckCircleIcon color="success" />
    ) : (
      <ErrorIcon color="error" />
    );
  };

  const getResolvedChip = (resolved?: boolean) => {
    const label = resolved === undefined ? t('common_na') : resolved ? t('common_yes') : t('common_no');
    const color = resolved === undefined ? 'default' : resolved ? 'success' : 'error';
    return <Chip label={label} color={color as any} size="small" />;
  };

  // Filter logic
  const filteredNotifications = notifications.filter((notification) => {
    const matchesSearch = (notification.featureTypeDescription || '')
      .toLowerCase()
      .includes(searchTerm.toLowerCase()) ||
      (notification.customerName && notification.customerName.toLowerCase().includes(searchTerm.toLowerCase()));

    const matchesFormat =
      filterFormat === 'all' || mapTypeDescToKey(notification.notificationTypeDescription) === filterFormat;

    const matchesSuccess =
      filterSuccess === 'all' ||
      (filterSuccess === 'success' && notification.success === true) ||
      (filterSuccess === 'error' && notification.success === false);

    return matchesSearch && matchesFormat && (isSuperUser ? matchesSuccess : true);
  });

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const getStats = () => {
    const total = notifications.length;
    const successful = notifications.filter((n) => n.success === true).length;
    const failed = notifications.filter((n) => n.success === false).length;
    const resolved = notifications.filter((n) => n.resolved === true).length;

    // Non-super users: show only Total
    if (!isSuperUser) {
      return (
        <Grid container spacing={3} sx={{ mb: 3 }}>
          <Grid component={'div' as React.ElementType} item xs={12} sm={6} md={4}>
            <Card>
              <CardContent>
                <Typography color="textSecondary" gutterBottom>
                  {t('notification_history_stats_total')}
                </Typography>
                <Typography variant="h4">{total}</Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      );
    }

    // Super Admin: show Total, Successes, Failures (and Resolved as extra)
    return (
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid component={'div' as React.ElementType} item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                {t('notification_history_stats_total')}
              </Typography>
              <Typography variant="h4">{total}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid component={'div' as React.ElementType} item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                {t('notification_history_stats_successes')}
              </Typography>
              <Typography variant="h4" color="success.main">
                {successful}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid component={'div' as React.ElementType} item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                {t('notification_history_stats_failures')}
              </Typography>
              <Typography variant="h4" color="error.main">
                {failed}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        {isSuperUser && (
          <Grid component={'div' as React.ElementType} item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Typography color="textSecondary" gutterBottom>
                  {t('notification_history_stats_resolved')}
                </Typography>
                <Typography variant="h4" color="info.main">
                  {resolved}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        )}
      </Grid>
    );
  };

  return (
    <MainLayout>
      <Box sx={{ flexGrow: 1 }}>
        <Typography
          variant="h4"
          component="h1"
          gutterBottom
          color="primary"
          sx={{ fontWeight: 'bold', mb: 4, display: 'flex', alignItems: 'center', gap: 1 }}
        >
          <HistoryIcon />
          {t('notification_history_title')}
        </Typography>
        <ApiConnectionAlert />

        {getStats()}

        {/* Filtros */}
        <Paper sx={{ p: 3, mb: 3 }}>
          <Grid container spacing={3} alignItems="center">
            <Grid component={'div' as React.ElementType} item xs={12} md={4}>
              <TextField
                fullWidth
                label={t('notification_history_filter_search_label')}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid component={'div' as React.ElementType} item xs={12} md={4}>
              <FormControl fullWidth>
                <InputLabel>{t('notification_history_filter_format_label')}</InputLabel>
                <Select
                  value={filterFormat}
                  label={t('notification_history_filter_format_label')}
                  onChange={(e) => setFilterFormat(e.target.value)}
                >
                  <MenuItem value="all">{t('notification_history_filter_format_all')}</MenuItem>
                  <MenuItem value="email">{t('notification_history_filter_format_email')}</MenuItem>
                  <MenuItem value="telegram">{t('notification_history_filter_format_telegram')}</MenuItem>
                  <MenuItem value="whatsapp">{t('notification_history_filter_format_whatsapp')}</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            {isSuperUser && (
              <Grid component={'div' as React.ElementType} item xs={12} md={4}>
                <FormControl fullWidth>
                  <InputLabel>{t('notification_history_filter_status_label')}</InputLabel>
                  <Select
                    value={filterSuccess}
                    label={t('notification_history_filter_status_label')}
                    onChange={(e) => setFilterSuccess(e.target.value)}
                  >
                    <MenuItem value="all">{t('notification_history_filter_status_all')}</MenuItem>
                    <MenuItem value="success">{t('notification_history_filter_status_success')}</MenuItem>
                    <MenuItem value="error">{t('notification_history_filter_status_error')}</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            )}
          </Grid>
        </Paper>

        {/* Tabela */}
        <Paper sx={{ width: '100%', overflow: 'hidden' }}>
          {notifications.length === 0 ? (
            <Box sx={{ p: 2 }}>
              <Alert severity="info" variant="outlined">
                {t('notification_history_empty_info')}
              </Alert>
            </Box>
          ) : (
            <>
              <TableContainer>
                <Table stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell>{t('notification_history_table_date')}</TableCell>
                      <TableCell>{t('notification_history_table_feature')}</TableCell>
                      {isSuperUser && (
                        <>
                          <TableCell>{t('notification_history_table_success')}</TableCell>
                          <TableCell>{t('notification_history_table_resolved')}</TableCell>
                          <TableCell>{t('notification_history_table_error_message')}</TableCell>
                          <TableCell>{t('notification_history_table_customer_name')}</TableCell>
                        </>
                      )}
                      {!isSuperUser && <TableCell>{t('notification_history_table_auth_format')}</TableCell>}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredNotifications
                      .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                      .map((notification) => (
                        <TableRow hover key={notification.requestId}>
                          <TableCell>
                            <Box>
                              <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                                {notification.createdDateFormatted}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                UTC: {new Date(notification.createdDate).toISOString().replace('T', ' ').slice(0, 19)}
                              </Typography>
                            </Box>
                          </TableCell>
                          <TableCell>{notification.featureTypeDescription}</TableCell>
                          {isSuperUser && (
                            <>
                              <TableCell>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                  {getSuccessIcon(!!notification.success)}
                                  {notification.success ? t('common_yes') : t('common_no')}
                                </Box>
                              </TableCell>
                              <TableCell>{getResolvedChip(notification.resolved)}</TableCell>
                              <TableCell>
                                <Box
                                  sx={{
                                    maxWidth: '200px',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                  }}
                                >
                                  {notification.messageError || '-'}
                                </Box>
                              </TableCell>
                              <TableCell>{notification.customerName || '-'}</TableCell>
                            </>
                          )}
                          {!isSuperUser && (
                            <TableCell>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                {getNotificationTypeIcon(notification.notificationTypeDescription)}
                                {getNotificationTypeLabel(notification.notificationTypeDescription)}
                              </Box>
                            </TableCell>
                          )}
                        </TableRow>
                      ))}
                  </TableBody>
                </Table>
              </TableContainer>
              <TablePagination
                rowsPerPageOptions={[5, 10, 25]}
                component="div"
                count={filteredNotifications.length}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                labelRowsPerPage={t('notification_history_pagination_rows_per_page')}
                labelDisplayedRows={({ from, to, count }) =>
                  count !== -1
                    ? t('notification_history_pagination_displayed_rows_of', { from, to, count })
                    : t('notification_history_pagination_displayed_rows_more_than', { from, to })
                }
              />
            </>
          )}
        </Paper
        >
      </Box>
    </MainLayout>
  );
};

export default NotificationHistory;